/**
 * hero.js — wirtschaft. am markt
 * ---------------------------------------------------------------------------
 * Drives the photo-reveal window of the hero (see hero.css for the layer
 * setup). Two phases:
 *   1. Intro sweep on load — a thin strip enters from the left, swells while
 *      travelling right, then settles as the design's resting window
 *      (photo over the left half, seam on the building's midline at 50%).
 *   2. Pointer follow — on fine-pointer devices a narrower strip tracks the
 *      cursor across the hero; leaving eases back to the resting window.
 * The window is written as --hero-clip-l / --hero-clip-r (percent insets)
 * so the CSS default keeps the resting state when JS never runs.
 */

(() => {
  "use strict";

  var REST = { c: 25, w: 50 }; // resting window: center / width in % → inset(0 50% 0 0)
  var STRIP_W = 30; // pointer-follow strip width in %
  var INTRO_MS = 2400;

  // Intro keyframes: t = progress, c = window center, w = window width (in %).
  var INTRO = [
    { t: 0, c: 2, w: 4 },
    { t: 0.42, c: 55, w: 34 },
    { t: 0.68, c: 80, w: 14 },
    { t: 1, c: REST.c, w: REST.w },
  ];

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  var finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");

  function easeInOutCubic(u) {
    return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
  }

  function initHero(root) {
    if (root.dataset.heroReady) return;
    root.dataset.heroReady = "true";

    var reveal = root.querySelector("[data-hero-reveal]");
    if (!reveal) return;

    // Reduced motion: freeze the background video on its poster frame too.
    if (reducedMotion.matches) {
      var video = root.querySelector("video");
      if (video) {
        video.removeAttribute("autoplay");
        video.pause();
      }
      return;
    }

    if (root.hasAttribute("data-hero-static")) return;

    var state = { c: REST.c, w: REST.w };
    var target = { c: REST.c, w: REST.w };
    var following = false;
    var introDone = false;
    var rafId = null;
    var lastTs = null;

    function apply() {
      var l = Math.max(0, Math.min(100, state.c - state.w / 2));
      var r = Math.max(0, Math.min(100, 100 - (state.c + state.w / 2)));
      reveal.style.setProperty("--hero-clip-l", l + "%");
      reveal.style.setProperty("--hero-clip-r", r + "%");
    }

    /* ---- Phase 1: intro sweep -------------------------------------------- */
    function playIntro() {
      // Jump to the sweep's start immediately so the resting CSS default
      // never flashes before the first animation frame.
      state.c = INTRO[0].c;
      state.w = INTRO[0].w;
      apply();
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / INTRO_MS, 1);
        var i = 1;
        while (i < INTRO.length - 1 && INTRO[i].t < p) i += 1;
        var a = INTRO[i - 1];
        var b = INTRO[i];
        var u = easeInOutCubic((p - a.t) / (b.t - a.t));
        state.c = a.c + (b.c - a.c) * u;
        state.w = a.w + (b.w - a.w) * u;
        apply();
        if (p < 1) {
          requestAnimationFrame(frame);
        } else {
          introDone = true;
        }
      }
      requestAnimationFrame(frame);
    }

    /* ---- Phase 2: pointer follow ------------------------------------------ */
    // Damped spring-ish lerp: the strip trails the cursor instead of sticking
    // to it. Width eases slower than position so entering/leaving breathes.
    function tick(ts) {
      var dt = lastTs === null ? 16 : Math.min(ts - lastTs, 64);
      lastTs = ts;
      var kc = 1 - Math.exp(-dt * 0.007);
      var kw = 1 - Math.exp(-dt * 0.0045);
      state.c += (target.c - state.c) * kc;
      state.w += (target.w - state.w) * kw;
      apply();

      var settled =
        !following &&
        Math.abs(target.c - state.c) < 0.05 &&
        Math.abs(target.w - state.w) < 0.05;
      if (settled) {
        state.c = target.c;
        state.w = target.w;
        apply();
        rafId = null;
        lastTs = null;
      } else {
        rafId = requestAnimationFrame(tick);
      }
    }

    function wake() {
      if (rafId === null) rafId = requestAnimationFrame(tick);
    }

    if (finePointer.matches) {
      root.addEventListener("pointermove", function (event) {
        if (!introDone) return;
        following = true;
        var rect = root.getBoundingClientRect();
        target.c = ((event.clientX - rect.left) / rect.width) * 100;
        target.w = STRIP_W;
        wake();
      });

      root.addEventListener("pointerleave", function () {
        if (!introDone) return;
        following = false;
        target.c = REST.c;
        target.w = REST.w;
        wake();
      });
    }

    playIntro();
  }

  function initAll(scope) {
    scope.querySelectorAll("[data-hero]").forEach(initHero);
  }

  initAll(document);

  // Theme editor: sections are re-rendered on setting changes.
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
